import { observable, runInAction } from 'mobx';
import bs58 from 'bs58';
import { WalletError, type ApiClient, type Addresses as ApiAddresses } from '@cef-ai/wallet-api-client';
import type { Identity, AccountInfo, Addresses, SessionResume, KeystoreEncryptor, WalletExport } from './types';
import type { CeremonyAdapter } from './ceremony';
import { createSessionVault, type SessionVault } from './vault';
import { derivedKeys, type DerivedKeys } from './derive';
import { signRegistrationProof } from './pop';
import { bytesToB64u } from './base64url';
import { deriveCereAddress, deriveSolanaAddress, deriveEvmAddress } from './addresses';
import { PRF_INPUT_SEED } from './constants';
import {
  deriveWrapKeyForVersion,
  RootUnwrapFailedError,
  UnsupportedWrapSourceError,
  type RootUnwrapper,
} from './rootWrap';
import { CrossTabSync } from './CrossTabSync';

export interface IdentityImplOptions {
  apiClient: ApiClient;
  ceremony: CeremonyAdapter;
  /**
   * SPA-injected keystore encoder (see `KeystoreEncryptor`). Optional at the
   * type level so lightweight test doubles need not supply one;
   * `exportKeystore` throws if it is absent. NOTE: in the running wallet SPA
   * this is always supplied — `createIdentity` defaults it to
   * `spaKeystoreEncryptor` — and the single `IdentityImpl` instance is shared
   * by both the `/settings` and `/embed/wallet` routes (one `WalletProvider`
   * wraps both), so this option is not how the standalone/embed boundary is
   * enforced. See `exportKeystore`'s doc comment for where that boundary
   * actually lives.
   */
  keystoreEncryptor?: KeystoreEncryptor;
  /**
   * Opens a non-origin credential's root envelope (see `login()`). Optional at
   * the type level so lightweight test doubles need not supply one — but a
   * non-origin login without it fails rather than falling back to the
   * credential's own PRF output, because that fallback IS the second-wallet
   * bug. `createIdentity` defaults it to `createSecretboxWrapper()`, mirroring
   * `keystoreEncryptor`.
   */
  rootUnwrapper?: RootUnwrapper;
}

/** Minimum slack before expiry where we treat the JWT as "needs refresh". */
const JWT_REFRESH_SLACK_MS = 60_000;

function decodeJwtExpMs(token: string): number {
  const parts = token.split('.');
  if (parts.length < 2) {
    throw new WalletError('validation', 'JWT does not have 3 parts');
  }
  const payloadB64 = parts[1];
  const payload = JSON.parse(Buffer.from(payloadB64.replace(/-/g, '+').replace(/_/g, '/'), 'base64').toString('utf8'));
  if (typeof payload.exp !== 'number') {
    throw new WalletError('validation', 'JWT missing exp');
  }
  return payload.exp * 1000;
}

function b64uDecode(s: string): Uint8Array {
  return Uint8Array.from(Buffer.from(s.replace(/-/g, '+').replace(/_/g, '/'), 'base64'));
}

/**
 * True only for the two shapes that mean "the server holds no envelope for
 * this credential": a 404, or a problem+json response that already carries the
 * `root-envelope-missing` URN (which `ApiClient.request()` has turned into
 * that `code`). Everything else — a 500, a 401, a network error — is a
 * transient or authorization fault whose diagnosis is NOT the credential.
 */
function isEnvelopeAbsence(e: unknown): boolean {
  return e instanceof WalletError && (e.code === 'root-envelope-missing' || e.status === 404);
}

/** Narrow a session response down to the account fields the identity exposes. */
function accountFromSession(session: {
  accountId: string;
  email: string | null;
  displayName: string | null;
}): AccountInfo {
  return { accountId: session.accountId, email: session.email, displayName: session.displayName };
}

export class IdentityImpl implements Identity, SessionResume, WalletExport {
  private readonly vault: SessionVault = createSessionVault();
  private cachedJwt: { token: string; expMs: number } | null = null;
  /** Server-returned credential ID (used as the public identity.credentialId). */
  private serverCredentialId: string | null = null;
  /**
   * Raw derived keys for the CURRENT session, captured at every `vault.set()`
   * call site (register / login / adoptSession). The vault deliberately hides
   * the seeds behind its closure (Spec §3.6 invariant 1); this field is the
   * single, explicit, same-origin-only seam that lets `exportSession()` hand a
   * live session to a sibling wallet-origin surface (the persistent iframe).
   * Cleared on logout / cross-tab logout alongside the vault. Never serialised
   * to storage and never sent to a host page.
   */
  private sessionKeys: DerivedKeys | null = null;
  /**
   * Absolute expiry (epoch ms) for `exportSession()`. Tracks the cached JWT
   * expiry after register/login; set from the adopted session in adoptSession.
   */
  private sessionExpMs: number | null = null;
  /**
   * Addresses for a bearer-active session (set by `resumeSession`, which has
   * no vault entry to read addresses from). Register/login/adoptSession keep
   * this nil and rely on the vault snapshot instead; `syncPublicState()`
   * falls back to this field only when the vault is closed.
   */
  private resumedAddresses: Addresses | null = null;
  /**
   * The S0 account behind the current session, as the server reported it on the
   * last session response (register-finish / login-finish / refresh). Non-
   * observable like `serverCredentialId`; pushed into `_account` by
   * `syncPublicState()`. Cleared on logout / cross-tab logout.
   */
  private currentAccount: AccountInfo | null = null;
  /**
   * De-dupes concurrent bearer-session refreshes. Refresh-token rotation +
   * reuse-detection means two parallel `session.refresh()` calls would replay a
   * rotated token and revoke the whole family, so `getJwt()`'s bearer fallback
   * shares a single in-flight refresh. Cleared once it settles.
   */
  private bearerRefreshInFlight: Promise<string> | null = null;

  // Public observable state — derived from vault snapshot + serverCredentialId.
  // Kept in sync via syncPublicState() so React `observer()` wrappers around
  // components reading isAuthenticated/addresses/credentialId actually re-render
  // on register/login/logout. The vault itself stays non-observable (security:
  // the closure-encapsulated keys must not be tracked by MobX).
  private readonly _isAuthenticated = observable.box(false);
  // `deep: false` keeps the stored value a plain object instead of wrapping it
  // in a MobX proxy. addresses is a wholesale-replaced snapshot (never mutated
  // field-by-field), so deep observability buys nothing — and a proxy is not
  // structured-cloneable, which breaks `postMessage` when the embed popup
  // bridge sends addresses to the host page (wallet:login:ok).
  private readonly _addresses = observable.box<Addresses | null>(null, { deep: false });
  private readonly _credentialId = observable.box<string | null>(null);
  // `deep: false` for the same reason as `_addresses`: this snapshot is
  // replaced wholesale and is read by the session-status handler, whose reply
  // is structured-cloned across `postMessage` to the host page. A MobX proxy is
  // not cloneable.
  private readonly _account = observable.box<AccountInfo | null>(null, { deep: false });

  /** Cross-tab logout coordination via BroadcastChannel('scp-wallet-v2'). */
  private readonly crossTabSync = new CrossTabSync();
  /** Handle to detach the cross-tab logout subscriber on dispose(). */
  private readonly crossTabSyncUnsubscribe: () => void;
  /** Idempotency flag for dispose(). */
  private disposed = false;

  constructor(private readonly opts: IdentityImplOptions) {
    // Hydrate the boxes from any existing vault snapshot. Currently the vault
    // starts closed, so this is a no-op — but it's the right call site for a
    // future BroadcastChannel-restored session.
    this.syncPublicState();
    // Cross-tab logout: when another tab broadcasts {type:'logout'}, mirror it
    // locally so this tab's MobX-bound UI re-renders without a page reload.
    // The guard prevents the originating tab from double-clearing — by the
    // time its own listener fires (BroadcastChannel doesn't deliver to the
    // sender, but defence-in-depth), local state is already cleared. The
    // guard also makes the listener a no-op for tabs that were already
    // signed out. Checks both the vault AND cachedJwt (not just
    // `vault.isOpen()`) so a bearer-active tab (Task 2: `resumeSession` —
    // cachedJwt set, vault closed, no keys) is also cleared by a peer's
    // logout broadcast; otherwise such a tab would stay authenticated.
    //
    // Capture the unsubscribe handle so dispose() can detach this subscriber
    // before closing the channel — without this, a long-lived test suite or
    // a hot-reloaded SPA leaks listener closures into the next IdentityImpl.
    this.crossTabSyncUnsubscribe = this.crossTabSync.subscribe((msg) => {
      if (msg.type === 'logout' && (this.vault.isOpen() || this.cachedJwt != null)) {
        this.vault.clear();
        this.cachedJwt = null;
        this.serverCredentialId = null;
        this.sessionKeys = null;
        this.sessionExpMs = null;
        this.resumedAddresses = null;
        this.currentAccount = null;
        this.syncPublicState();
      }
    });
  }

  get isAuthenticated(): boolean {
    return this._isAuthenticated.get();
  }

  get addresses(): Addresses | null {
    return this._addresses.get();
  }

  /**
   * Returns the server-returned credentialId from the last register/login ceremony.
   * The vault internally tracks the authenticator-generated credential ID for login use.
   */
  get credentialId(): string | null {
    return this._credentialId.get();
  }

  /**
   * The account behind the current session, or `null` when signed out. Comes
   * straight off the server's session response — reading it never runs a
   * ceremony, which is what lets `wallet:session:status` report it.
   */
  get account(): AccountInfo | null {
    return this._account.get();
  }

  /**
   * Discriminates the two authenticated states: `true` when the session has
   * derived signing keys (register/login/adoptSession); `false` for a
   * bearer-active session adopted via `resumeSession` (JWT only). Token-
   * authorized ops (e.g. delegation mint) only require `isAuthenticated`;
   * anything that needs a raw signature must check `hasKeys()` first and
   * step up (re-run login) when it's false.
   */
  hasKeys(): boolean {
    return this.sessionKeys != null;
  }

  /**
   * Read the (non-observable) vault snapshot + cached server credential ID and
   * push the values into the three public observable boxes. Call after any
   * mutation that could change the public derived state: register, login,
   * logout, or constructor (hydration).
   */
  private syncPublicState(): void {
    const snap = this.vault.snapshot();
    // isAuthenticated is bearer-driven: a present, unexpired cachedJwt is
    // sufficient on its own (Task 2 — "bearer-active" state, no keys). Keys
    // are optional; use hasKeys() to discriminate whether signing is
    // possible without step-up.
    //
    // `adoptSession()` (register-popup → iframe handoff, pre-dates Task 2) is
    // the one path that installs keys WITHOUT a JWT — there is no ceremony,
    // so no token is minted; the session is authorized purely by the
    // transported key + its own expiry (`sessionExpMs`). It must keep
    // satisfying isAuthenticated, so this is an OR, not a replacement:
    // authenticated if the JWT is live, or if we hold keys that haven't
    // expired. Login/register set cachedJwt+keys+sessionExpMs together, so
    // both existing call sites keep tripping this true exactly as before.
    const jwtLive = this.cachedJwt != null && Date.now() < this.cachedJwt.expMs;
    const keysLive = this.sessionKeys != null && this.sessionExpMs != null && Date.now() < this.sessionExpMs;
    const isAuthenticated = jwtLive || keysLive;
    // Addresses can come from either the vault snapshot (keys present) or a
    // bearer-active resumed session (no vault entry, addresses cached
    // separately in `resumedAddresses`).
    runInAction(() => {
      this._isAuthenticated.set(isAuthenticated);
      this._addresses.set(snap?.addresses ?? this.resumedAddresses);
      this._credentialId.set(this.serverCredentialId);
      this._account.set(this.currentAccount);
    });
  }

  /**
   * Create a NEW account and enrol this device's passkey as its ORIGIN
   * credential: the PRF output of a credential registered here IS the account
   * root (`derive.ts` uses it verbatim as the ed25519 seed), which is exactly
   * what `is_origin` records server-side. Registration therefore writes NO root
   * envelope — there is no other root to wrap. Adding a SECOND device is
   * recover-then-enrol (a later sub-project), never a second `register()`.
   */
  async register(opts: { emailToken: string; displayName?: string; email?: string; label?: string }): Promise<void> {
    // 0. S0: the mailbox is proven BEFORE the ceremony. `emailToken` is
    //    required by the type, so this guard is defence in depth — it still
    //    catches an empty string, and any JS caller the compiler never saw.
    //    Rejecting here (rather than sending a request the server must reject
    //    anyway) keeps the failure named, and keeps it BEFORE the ceremony, so
    //    there is no half-registration and no authenticator prompt to abandon.
    if (!opts.emailToken) {
      throw new WalletError(
        'email-unverified',
        'register() requires an emailToken from accounts.emailVerify(); verify the email before the ceremony',
      );
    }

    // 1. Start ceremony — get the challenge from the API. No addresses go up
    //    here: they are only knowable after the ceremony produces a PRF output,
    //    and /finish is where the server binds them (via proof-of-possession).
    const start = await this.opts.apiClient.passkey.registerStart({
      emailToken: opts.emailToken,
      displayName: opts.displayName,
      label: opts.label,
    });

    // 2. Run the ceremony.
    const cer = await this.opts.ceremony.register({
      rpId: start.rpId,
      challenge: b64uDecode(start.challenge),
      userHandle: b64uDecode(start.userHandle),
      prfInput: PRF_INPUT_SEED,
      label: opts.label,
      // The WebAuthn user entity is what the OS passkey manager shows: `name`
      // is the account line, `displayName` the friendly one. Both are hints to
      // the authenticator — they are absent from `clientDataJSON` and from the
      // attestation, so neither reaches the server and the address here is not
      // a second, unverified copy of the one `emailToken` attests. Passing it
      // is the whole difference between an entry that reads
      // `alex@example.com` and one that reads `scp-wallet` (CEF-AI/wallet#72).
      name: opts.displayName,
      // `|| undefined`, not `??`: an empty string is not nullish, so it would
      // reach the adapter and win its `opts.email ?? opts.label ?? 'scp-wallet'`
      // fallback chain — producing a passkey entry with a BLANK account line,
      // which is worse than the `scp-wallet` this fix exists to remove. Neither
      // in-repo caller can produce one; this is the same defence-in-depth the
      // empty-`emailToken` guard above is, for JS callers the compiler never saw.
      email: opts.email || undefined,
    });
    if (!cer.prfOutput) {
      throw new WalletError('prf-unsupported', 'authenticator did not return a PRF output');
    }

    // 3. Derive keys + addresses locally.
    const keys = derivedKeys(cer.prfOutput);
    const clientAddresses: Addresses = {
      cere: deriveCereAddress(keys.edPubkey),
      solana: deriveSolanaAddress(keys.edPubkey),
      evm: deriveEvmAddress(keys.secpPubkeyUncompressed),
    };

    // 4. Prove possession of the PRF-derived chain keys by signing the
    //    single-use challenge. The server binds addresses.{cere,solana,evm}
    //    to these signatures (proof-of-possession), not to the WebAuthn
    //    credential key. Spec §3.3 (revised).
    const proof = signRegistrationProof(keys, b64uDecode(start.challenge));

    // 5. Finish ceremony with the API.
    const finish = await this.opts.apiClient.passkey.registerFinish({
      challengeId: start.challengeId,
      clientDataJSON: cer.clientDataJSON,
      attestationObject: cer.attestationObject,
      addresses: clientAddresses,
      transports: cer.transports,
      identitySig: bytesToB64u(proof.identitySig),
      evmPubkey: bytesToB64u(proof.evmPubkey),
      evmSig: bytesToB64u(proof.evmSig),
    });

    // 6. Cross-check returned addresses against locally-derived. Spec §3.3.
    //    This doubles as the guard that a registration really did produce an
    //    origin credential: the keys above come from `cer.prfOutput` directly,
    //    so if the server had attached this credential to an EXISTING account
    //    (whose root is some other credential's PRF output) it would answer
    //    with that account's addresses and this comparison would fail loudly —
    //    rather than us installing a second, silently-wrong wallet.
    this.crossCheckAddresses(clientAddresses, finish.addresses);

    // 7. Install the vault + cache the JWT.
    //    The vault stores the authenticator-generated credential ID (cer.credentialId)
    //    so subsequent login() calls can pass it back to the same authenticator.
    //    The server-returned credential ID is tracked separately for the public getter.
    this.vault.set({
      keys,
      addresses: clientAddresses,
      credentialId: cer.credentialId,
    });
    this.serverCredentialId = finish.credentialId;
    this.currentAccount = accountFromSession(finish);
    this.cachedJwt = { token: finish.token, expMs: decodeJwtExpMs(finish.token) };
    // Capture the session for a possible same-origin handoff (register popup →
    // iframe). See `sessionKeys` field + `exportSession()`.
    this.sessionKeys = keys;
    this.sessionExpMs = this.cachedJwt.expMs;
    this.syncPublicState();
  }

  async login(opts: { credentialId?: string } = {}): Promise<void> {
    const start = await this.opts.apiClient.passkey.loginStart();

    const cer = await this.opts.ceremony.login({
      rpId: start.rpId,
      challenge: b64uDecode(start.challenge),
      // An explicit pin (step-up of a known session) takes precedence over
      // the vault snapshot; the vault is closed during a step-up anyway
      // (there is nothing to fall back to), but an explicit caller-supplied
      // pin should win even if the vault happened to hold something.
      credentialId: opts.credentialId ?? this.vault.snapshot()?.credentialId,
      prfInput: PRF_INPUT_SEED,
    });
    if (!cer.prfOutput) {
      throw new WalletError('prf-unsupported', 'authenticator did not return a PRF output');
    }

    const finish = await this.opts.apiClient.passkey.loginFinish({
      challengeId: start.challengeId,
      credentialId: cer.credentialId,
      clientDataJSON: cer.clientDataJSON,
      authenticatorData: cer.authenticatorData,
      signature: cer.signature,
    });

    // The JWT is installed BEFORE the root is resolved, because resolving a
    // non-origin root calls `accounts.getEnvelope` — an authRequired endpoint
    // whose token comes back through `createIdentity`'s
    // `getAuthToken: () => identity.getJwt()`. With no cached token that
    // re-entry sees a closed vault and throws `unauthorized`, so the envelope
    // fetch would fail as an auth error instead of fetching. Kept in a local so
    // any failure below can put the previous token back: a login that cannot
    // resolve its root must not leave behind a half-authenticated session whose
    // `getJwt()` succeeds for an identity holding no keys.
    const previousJwt = this.cachedJwt;
    this.cachedJwt = { token: finish.token, expMs: decodeJwtExpMs(finish.token) };

    let keys: DerivedKeys;
    let clientAddresses: Addresses;
    try {
      // `isOrigin` means THIS credential's PRF output IS the account root
      // (`derive.ts` uses it verbatim as the ed25519 seed). Every other
      // credential's PRF output is a WRAPPING key only: deriving from it
      // directly would yield a different, valid-looking address — a second
      // wallet created silently, which no database fix can repoint because
      // bucket ownership is recorded on-chain. This branch is the whole reason
      // the S0 account model exists.
      // The envelope is looked up by the AUTHENTICATOR-side `cer.credentialId`,
      // while this class tracks the server-assigned id separately. Known
      // asymmetry, not an oversight: see wallet#58. It fails CLOSED (a lookup
      // by the wrong id finds no envelope and login fails loudly rather than
      // deriving a wrong root), and choosing which id is canonical is a
      // device-add-flow decision, not an S0 one.
      const root = finish.isOrigin
        ? cer.prfOutput
        : await this.unwrapRootForCredential(cer.prfOutput, cer.credentialId);

      keys = derivedKeys(root);
      clientAddresses = {
        cere: deriveCereAddress(keys.edPubkey),
        solana: deriveSolanaAddress(keys.edPubkey),
        evm: deriveEvmAddress(keys.secpPubkeyUncompressed),
      };

      // Deliberately AFTER the root resolution, not before it: derivation now
      // runs on the root we actually resolved, so on the non-origin branch this
      // compares addresses from the UNWRAPPED root against the account the
      // server says we are. That comparison is what makes a wrong root loud.
      // Run before the resolution (as it was), it would instead reject every
      // added device — the client addresses would be the wrong ones.
      this.crossCheckAddresses(clientAddresses, finish.addresses);
    } catch (e) {
      this.cachedJwt = previousJwt;
      throw e;
    }

    this.vault.set({
      keys,
      addresses: clientAddresses,
      credentialId: cer.credentialId,
    });
    this.serverCredentialId = finish.credentialId;
    this.currentAccount = accountFromSession(finish);
    this.sessionKeys = keys;
    this.sessionExpMs = this.cachedJwt.expMs;
    this.syncPublicState();
  }

  /**
   * Resolve the account root for a NON-origin credential by unwrapping that
   * credential's root envelope. `prfOutput` here is a wrapping key, never a
   * seed.
   *
   * The ways this fails stay distinct, diagnosable conditions — that is what
   * `wrapSource` and `wrapVersion` on the wire are for, and collapsing them
   * would leave a caller unable to tell "this device was never enrolled" from
   * "upgrade this client" from "use your recovery password" from "this envelope
   * is damaged". Every one of them is a `WalletError` subclass, so a caller can
   * switch on `.code` and the SPA can render real copy:
   *
   *   1. no envelope for the credential — a 404, or the `root-envelope-missing`
   *      URN — → `WalletError('root-envelope-missing')`, with the underlying
   *      failure kept on `cause`. Any OTHER failure of that fetch (a 500, a
   *      401, a network error) is not this condition and propagates as
   *      whatever `ApiClient` already made of it;
   *   2. an envelope that is not credential-PRF-wrapped →
   *      `UnsupportedWrapSourceError` (`root-wrap-source-unsupported`);
   *   3. a `wrapVersion` outside this build's compiled-in set →
   *      `UnrecognisedWrapVersionError` (`root-wrap-version-unknown`),
   *      propagated untouched from `deriveWrapKeyForVersion` (which is also the
   *      only place a server-supplied version string is turned into
   *      key-derivation input);
   *   4. ciphertext that will not open under the derived key →
   *      `RootUnwrapFailedError` (`root-unwrap-failed`), carrying the
   *      primitive's error as `cause`.
   *
   * Each `catch` wraps exactly one call for exactly that reason; there is no
   * blanket try around the whole method.
   */
  private async unwrapRootForCredential(prfOutput: Uint8Array, credentialId: string): Promise<Uint8Array> {
    const unwrapper = this.opts.rootUnwrapper;
    if (!unwrapper) {
      // Checked before the fetch: nothing here can succeed without it, and
      // failing first spares an authenticated round trip. Failing at all --
      // rather than falling back to `prfOutput` as a seed -- is the point: that
      // fallback IS the silent-second-wallet bug.
      throw new WalletError('validation', 'login: no rootUnwrapper configured; cannot open a non-origin root envelope');
    }
    let envelope;
    try {
      envelope = await this.opts.apiClient.accounts.getEnvelope(credentialId);
    } catch (e) {
      if (!isEnvelopeAbsence(e)) {
        // A 500, a 401, a network error: the wallet service failed, and the
        // credential is not implicated. `ApiClient.request()` has already
        // translated the response into a `WalletError` with the right code
        // (`internal`, `unauthorized`, ...), so the correct move is to let it
        // through untouched. Rewriting it to `root-envelope-missing` told the
        // reader their passkey "isn't set up to open your wallet" — the wrong
        // diagnosis AND the wrong remedy for a fault that clears on retry, and
        // one that teaches distrust of a passkey that works perfectly.
        throw e;
      }
      // A genuine absence, and the only case this code belongs on. Re-raised
      // rather than re-thrown so the message names the credential, with the
      // original error on `cause` so nothing is lost (the pattern
      // `RootUnwrapFailedError` uses below).
      const missing = new WalletError(
        'root-envelope-missing',
        `no root envelope for non-origin credential ${credentialId}`,
      );
      (missing as Error).cause = e;
      throw missing;
    }
    // A `password` envelope belongs to the recovery path and cannot be opened
    // with a credential key. Rejecting it here rather than letting the unwrap
    // fail keeps a FOURTH condition distinct: attacking it with the PRF-derived
    // key is fail-safe (it will not open, so no wrong root is derived), but it
    // would be reported as a damaged envelope when the real answer is "this is
    // the wrong kind of envelope".
    if (envelope.wrapSource !== 'credential-prf') {
      throw new UnsupportedWrapSourceError(envelope.wrapSource, credentialId);
    }
    // Resolved through the closed version → label map, never from the wire
    // string itself. Throws `UnrecognisedWrapVersionError`, which is left to
    // propagate as itself.
    const wrapKey = deriveWrapKeyForVersion(prfOutput, envelope.wrapVersion);
    try {
      return unwrapper.unwrap(b64uDecode(envelope.ciphertext), b64uDecode(envelope.nonce), wrapKey);
    } catch (e) {
      throw new RootUnwrapFailedError(credentialId, { cause: e });
    } finally {
      // Wipe the wrap key on every exit, success or failure. It is a fresh
      // HKDF output owned by this call and aliased by nothing: `unwrap`
      // returns a COPY of the plaintext (`Uint8Array.from` in
      // `createSecretboxWrapper`), so clearing the key cannot reach the value
      // being returned. `keyBytes.fill(0)` in `exportKeystore` bounds a
      // plaintext key copy the same way.
      //
      // The boundary, stated so the remaining asymmetry is deliberate rather
      // than missed: the ROOT is NOT wiped here. It is this method's return
      // value — the caller derives the session keys from it, and those live in
      // the vault, which owns their zeroization (`vault.ts` wipes `edSeed` /
      // `secpKey` on close). `prfOutput` is likewise the caller's, and this
      // method is not the end of its life. Only the wrap key dies here.
      wrapKey.fill(0);
    }
  }

  /**
   * Adopt a session from a token-refresh response (`apiClient.session.refresh()`)
   * WITHOUT running a passkey ceremony and WITHOUT deriving keys — the
   * "bearer-active" state (Task 2). Sets `cachedJwt` + its expiry + addresses;
   * leaves `sessionKeys` (and the vault) untouched, so `hasKeys()` stays
   * false unless a prior login already populated them. Callers needing a raw
   * signature must step up via `login()` first; token-authorized ops (e.g.
   * delegation mint) only need `isAuthenticated`, which this satisfies.
   */
  resumeSession(session: {
    token: string;
    addresses: Addresses;
    credentialId: string;
    accountId?: string;
    email?: string | null;
    displayName?: string | null;
  }): void {
    this.cachedJwt = { token: session.token, expMs: decodeJwtExpMs(session.token) };
    this.serverCredentialId = session.credentialId;
    this.resumedAddresses = session.addresses;
    // A bearer-resumed tab knows its account without a ceremony — the refresh
    // response carries the same account fields a login-finish does, so
    // `identity.account` is populated on a cold reload too. Only overwritten
    // when the caller actually supplied an account: an older call site that
    // passes just token/addresses/credentialId should not blank a known one.
    if (session.accountId != null) {
      this.currentAccount = {
        accountId: session.accountId,
        email: session.email ?? null,
        displayName: session.displayName ?? null,
      };
    }
    this.syncPublicState();
  }

  /**
   * Replace the account fields for the CURRENT session, without a ceremony and
   * without touching keys, the vault, or the JWT.
   *
   * The one caller is `PATCH /v1/accounts/me`, which returns the full updated
   * account. Everything else that sets `currentAccount` does so from a session
   * response (register-finish / login-finish / refresh), and a patch produces
   * none — so without this, an account that has just bound an email keeps
   * reporting `email: null` from `identity.account` until the next session
   * response, and every reader of that field (the Settings nudge, the
   * `wallet:session:status` payload) is working from a value the client itself
   * knows to be stale.
   *
   * Deliberately narrow: it writes `currentAccount` and re-syncs public state,
   * nothing else. It is NOT a login — a call with no live session is ignored
   * rather than resurrecting an account onto a signed-out identity, which is
   * what a logout racing an in-flight patch would otherwise do.
   */
  setAccount(account: AccountInfo): void {
    if (!this.cachedJwt) return;
    this.currentAccount = { ...account };
    this.syncPublicState();
  }

  logout(): void {
    this.vault.clear();
    this.cachedJwt = null;
    this.serverCredentialId = null;
    this.sessionKeys = null;
    this.sessionExpMs = null;
    this.resumedAddresses = null;
    this.currentAccount = null;
    this.syncPublicState();
    // Broadcast AFTER clearing local state so other tabs converge on the same
    // post-logout snapshot. BroadcastChannel does not deliver to the sender,
    // so this tab won't re-enter the subscriber callback above.
    this.crossTabSync.broadcast({ type: 'logout' });
  }

  async getJwt(): Promise<string> {
    if (this.cachedJwt && Date.now() < this.cachedJwt.expMs - JWT_REFRESH_SLACK_MS) {
      return this.cachedJwt.token;
    }
    // The wallet-origin refresh cookie can re-mint the JWT with no ceremony,
    // and `refreshBearerSession` lands in `resumeSession()`, which touches only
    // cachedJwt / serverCredentialId / resumedAddresses — never the vault and
    // never `sessionKeys`. So this path is safe for a WARM session (vault open)
    // too, not just a bearer one, and trying it FIRST is what stops an idle
    // keyed tab from prompting at the ~20m JWT TTL.
    //
    // It used to be the other way round: a bearer session refreshed silently
    // while a warm one was sent to `login()` — a real WebAuthn ceremony — which
    // made the tab HOLDING the keys the one that interrupts the user. That is
    // the reported idle-tab symptom.
    if (this.cachedJwt) {
      try {
        return await this.refreshBearerSession();
      } catch {
        // Cookie missing/expired/revoked — or the API is genuinely down, since
        // `session.refresh()` already retried a 5xx once and gave up
        // (CEF-AI/wallet#60). A warm session can still re-mint via a ceremony;
        // a keyless one cannot, and falls through to unauthorized. During a
        // real outage the ceremony dies at `passkey/login/start`, before any
        // prompt is shown — the passkey prompt this path is accused of is
        // reachable only when the fault clears between the two calls, which is
        // the window the retry closes.
      }
    }
    // Warm session (vault open): re-run login to mint a fresh JWT (+ keys).
    // Now the FALLBACK for a dead cookie rather than the first move.
    if (this.vault.isOpen()) {
      await this.login();
      return this.cachedJwt!.token;
    }
    throw new WalletError('unauthorized', 'session is closed; call register() or login() first');
  }

  /**
   * Silently re-mint the bearer session's JWT from the wallet-origin refresh
   * cookie, de-duped across concurrent callers. Refresh-token rotation means two
   * parallel refreshes would replay a rotated token → reuse-detection revokes
   * the family; sharing one in-flight promise avoids that. Resolves to the fresh
   * JWT; rejects (propagating the `session.refresh()` error) if the cookie is
   * missing/expired/revoked.
   */
  private refreshBearerSession(): Promise<string> {
    if (!this.bearerRefreshInFlight) {
      this.bearerRefreshInFlight = this.opts.apiClient.session
        .refresh()
        .then((resp) => {
          // `SessionResponse.addresses` is the wire shape (solana/evm optional);
          // `resumeSession` needs a complete tri-chain `Addresses`. Every real
          // session has all three (register derives cere+solana+evm), so a
          // partial response is malformed — fail closed (→ `unauthorized`)
          // rather than adopting a half-populated identity. Mirrors the SPA
          // boot-resume completeness guard.
          const { cere, solana, evm } = resp.addresses;
          if (!cere || !solana || !evm) {
            throw new WalletError('unauthorized', 'session refresh returned incomplete addresses');
          }
          this.resumeSession({
            token: resp.token,
            addresses: { cere, solana, evm },
            credentialId: resp.credentialId,
            accountId: resp.accountId,
            email: resp.email,
            displayName: resp.displayName,
          });
          // `resumeSession` deliberately leaves the vault and `sessionKeys`
          // alone, which is what makes it safe for a WARM caller — but
          // `sessionExpMs` still has to track the new JWT when we hold keys.
          // `login()` advanced it as a side effect of the ceremony; now that a
          // warm session refreshes silently instead, nothing else would, and
          // `exportSession()` gates on it. Leaving it stale would let a warm
          // tab stay authenticated while quietly losing the ability to hand its
          // session to the persistent iframe — a failure with no error. Nil for
          // a bearer session, which holds no keys to export.
          if (this.sessionKeys != null && this.cachedJwt != null) {
            this.sessionExpMs = this.cachedJwt.expMs;
            this.syncPublicState();
          }
          return resp.token;
        })
        .finally(() => {
          this.bearerRefreshInFlight = null;
        });
    }
    return this.bearerRefreshInFlight;
  }

  /**
   * Read the cached JWT verbatim — no expiry check, no refresh. A cache
   * INSPECTOR, for tests and diagnostics.
   *
   * NOT the token source for `ApiClient.getAuthToken`. It was, under the name
   * `getCachedJwtForApiClient`, and that is what caused the outage this rename
   * exists to prevent: the JWT TTL (~20m) is far shorter than a tab's life, so
   * every authRequired call from an idle tab shipped a long-dead token and
   * wallet-api answered `token-expired` — permanently, because nothing on that
   * path refreshed. Anything that needs a token to SEND must call `getJwt()`,
   * which refreshes within `JWT_REFRESH_SLACK_MS` (silently, off the refresh
   * cookie, for a bearer-active session). See `src/bootstrap/createIdentity.ts`
   * and its `createIdentity.authToken.test.ts`.
   */
  peekCachedJwt(): string | null {
    return this.cachedJwt?.token ?? null;
  }

  /**
   * Returns the session vault. Used by popup-side `wallet:sign` handlers
   * which need direct access to `vault.sign(chain, payload)`. NOT for host-
   * side use — keys never reach the host. Spec §3.6 invariant 1.
   */
  getVault(): SessionVault {
    return this.vault;
  }

  /*
   * S1 PR2 deleted `exportSession()`. It returned raw `edSeed`/`secpKey` bytes
   * to any same-origin caller so a sibling wallet surface could adopt the
   * session. Nothing needs it now: the frame re-derives its own key from PRF
   * after a registration popup, so no session is ever handed over.
   *
   * `exportKeystore` below is NOT the same thing and deliberately survives —
   * it returns an ENCRYPTED keystore the caller must supply a passphrase to
   * open, which is the user exporting their own key on purpose.
   */

  /**
   * Export the current account's key for `chain` as a password-encrypted
   * keystore JSON string. The raw seed is read from the `sessionKeys` seam,
   * handed to the injected `KeystoreEncryptor`, and only the returned ciphertext
   * escapes — Spec §3.6 containment holds.
   *
   * This method has no notion of "standalone" vs "embed" surface: one
   * `WalletProvider` wraps both the `/settings` and `/embed/wallet` routes,
   * `createIdentity` always defaults the `keystoreEncryptor`, and the same
   * `IdentityImpl` instance backs both — so this call is exactly as reachable
   * from the embed popup as from Settings. The standalone-only boundary is
   * enforced above this layer: `ExportAccount` is only mounted by the
   * `/settings` route, and it additionally asserts `!isEmbedSurface()` at
   * runtime (`src/wallet/export/isEmbedSurface.ts`) as a belt-and-suspenders
   * check. Do not rely on this method to gate the surface.
   */
  async exportKeystore(chain: 'cere' | 'evm', passphrase: string): Promise<string> {
    if (!this.vault.isOpen() || !this.sessionKeys) {
      throw new WalletError('unauthorized', 'exportKeystore: no open session');
    }
    if (!passphrase) {
      throw new WalletError('validation', 'exportKeystore: passphrase is required');
    }
    const encryptor = this.opts.keystoreEncryptor;
    if (!encryptor) {
      throw new WalletError('validation', 'exportKeystore: no keystore encryptor configured');
    }
    // Read only for the address below — `VaultSnapshot` is `{addresses, credentialId}`
    // and never carries key bytes (see packages/identity/src/vault.ts). The key
    // material comes exclusively from `this.sessionKeys`.
    const snap = this.vault.snapshot();
    if (!snap) {
      throw new WalletError('unauthorized', 'exportKeystore: vault snapshot unavailable');
    }
    // Copy so the encryptor cannot retain a handle to our live key buffer.
    const keyBytes =
      chain === 'cere' ? new Uint8Array(this.sessionKeys.edSeed) : new Uint8Array(this.sessionKeys.secpKey);
    const address = chain === 'cere' ? snap.addresses.cere : snap.addresses.evm;
    try {
      return await encryptor(chain, keyBytes, passphrase, address);
    } finally {
      // Zero the copy synchronously on the way out. `keyBytes` is the exact
      // reference the encryptor received; per the `KeystoreEncryptor` contract
      // it must be pure and retain nothing, so it is done with the buffer by
      // the time this runs. Wiping here bounds the plaintext key copy's
      // lifetime to this call rather than leaving it for GC. Callers that need
      // to inspect the bytes handed to the encryptor must snapshot them inside
      // the encryptor (the buffer is live for the whole encryptor invocation),
      // not read the argument reference afterwards.
      keyBytes.fill(0);
    }
  }

  /**
   * Reveal the Solana secret key as base58 (64 bytes: seed || pubkey). This is
   * the ONE raw-key export path — Phantom cannot import an encrypted file. It is
   * a deliberately separate, explicitly-named method so the raw path is never
   * taken by accident. As with `exportKeystore`, this method itself does not
   * know the standalone/embed surface; that boundary is enforced by the UI
   * (see `exportKeystore`'s doc comment).
   */
  async revealSolanaSecret(): Promise<string> {
    if (!this.vault.isOpen() || !this.sessionKeys) {
      throw new WalletError('unauthorized', 'revealSolanaSecret: no open session');
    }
    const seed = this.sessionKeys.edSeed;
    const pub = this.sessionKeys.edPubkey;
    const secret = new Uint8Array(64);
    secret.set(seed, 0);
    secret.set(pub, 32);
    // Copy is local to this call; the live session keys (`this.sessionKeys.edSeed`
    // / `edPubkey`) are untouched. Wiped here for symmetry with `exportKeystore`'s
    // `keyBytes.fill(0)` — bounds this plaintext copy's lifetime to the call.
    const encoded = bs58.encode(secret);
    secret.fill(0);
    return encoded;
  }

  /*
   * S1 PR2 deleted `adoptSession()`, the consumer half of the seam above. It
   * rebuilt derived keys from transported seeds and opened the vault WITHOUT a
   * WebAuthn ceremony — the one path by which a surface could hold keys it had
   * not derived itself. See `no-key-export.test.ts`.
   */

  /**
   * Release the BroadcastChannel + cross-tab subscriber. Idempotent.
   *
   * Called by:
   *   - WalletProvider's useEffect cleanup (SPA unmount / apiBaseUrl change)
   *   - test afterEach hooks (prevent listener leakage across tests)
   *
   * After dispose(), the identity is unusable: register/login/logout/getJwt
   * still execute their normal logic, but cross-tab broadcasts no longer
   * propagate (channel is closed) and the local subscriber is detached.
   * The vault remains cleared (logout() runs locally). Spec §9.4 lifecycle.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    // Detach subscriber first so any in-flight onmessage from this channel
    // does not re-enter the now-closing identity.
    this.crossTabSyncUnsubscribe();
    this.crossTabSync.close();
  }

  // ---- private --------------------------------------------------------------

  private crossCheckAddresses(client: Addresses, server: ApiAddresses | undefined): void {
    if (!server) {
      throw new WalletError('derivation-mismatch', 'API did not return addresses');
    }
    // `cere` is required by the wire type, so make the runtime agree. Guarded
    // like the optional chains below, a response of `{cere: null}` would skip
    // EVERY comparison and let the login succeed having checked nothing — and
    // on the non-origin branch this comparison is the one thing standing
    // between a wrong root and a silently created second wallet. A cross-check
    // that can quietly no-op is not a cross-check.
    if (!server.cere) {
      throw new WalletError('derivation-mismatch', 'API returned no Cere address to cross-check against');
    }
    if (client.cere !== server.cere) {
      throw new WalletError('derivation-mismatch', `Cere mismatch: client=${client.cere} api=${server.cere}`);
    }
    if (server.solana != null && client.solana !== server.solana) {
      throw new WalletError('derivation-mismatch', `Solana mismatch: client=${client.solana} api=${server.solana}`);
    }
    if (server.evm != null && client.evm.toLowerCase() !== server.evm.toLowerCase()) {
      throw new WalletError('derivation-mismatch', `EVM mismatch: client=${client.evm} api=${server.evm}`);
    }
  }
}
